import { useState, useCallback, useRef } from 'react';
import { useArbiterClient } from '../context/ArbiterClientContext';
import type { GuidanceSearchResult, RetrieverType } from '@amzn/helianthus-arbiter-client';

interface UseGuidanceSearchResult {
  results: GuidanceSearchResult[];
  retrieverUsed: RetrieverType | null;
  loading: boolean;
  error: string | null;
  search: (query: string, maxResults?: number, retriever?: RetrieverType) => void;
}

export function useGuidanceSearch(): UseGuidanceSearchResult {
  const client = useArbiterClient();
  const [results, setResults] = useState<GuidanceSearchResult[]>([]);
  const [retrieverUsed, setRetrieverUsed] = useState<RetrieverType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const search = useCallback(
    (query: string, maxResults?: number, retriever?: RetrieverType) => {
      if (!query.trim()) {
        ++requestIdRef.current;
        setResults([]);
        setRetrieverUsed(null);
        setError(null);
        setLoading(false);
        return;
      }

      const currentRequestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);
      setResults([]);
      setRetrieverUsed(null);

      client
        .searchGuidance({
          query,
          ...(maxResults != null ? { maxResults } : {}),
          ...(retriever != null ? { retriever } : {}),
        })
        .then((response) => {
          if (requestIdRef.current !== currentRequestId) return;
          setResults(response.results ?? []);
          setRetrieverUsed(response.retrieverUsed ?? null);
        })
        .catch((err) => {
          if (requestIdRef.current !== currentRequestId) return;
          setError(err instanceof Error ? err.message : 'Search failed');
        })
        .finally(() => {
          if (requestIdRef.current === currentRequestId) {
            setLoading(false);
          }
        });
    },
    [client],
  );

  return { results, retrieverUsed, loading, error, search };
}
